import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Button,
  Alert,
  Snackbar,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Card,
  CardContent,
  Typography,
  Grid,
  Chip,
  Select,
  MenuItem,
  FormControl,
  InputLabel
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import PackageIcon from '@mui/icons-material/Inventory';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PendingIcon from '@mui/icons-material/Schedule';
import { useInventoryPage } from '../contexts/InventoryPageContext';
import { useInventory } from '../contexts/InventoryContext';
import { useJob } from '../contexts/JobContext';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api.service';

// Import the new step components
import ReceiveBarcodeStep from './ReceiveBarcodeStep';
import ReceiveEquipmentStep from './ReceiveEquipmentStep';
import ReceiveLocationStep from './ReceiveLocationStep';
import ReceiveDetailsStep from './ReceiveDetailsStep';
import ReceiveRecentItems from './ReceiveRecentItems';
import { printQRCodes, printSingleQRCode } from '../services/qrCodePrintService';
import StatusHistoryDialog from './StatusHistoryDialog';
import LinkifiedText from './LinkifiedText';

const ReceiveInventory = () => {
  // Context data
  const { warehouses, racks, bays } = useInventoryPage();
  const { loadInventorySummary } = useInventory();
  const { currentJob } = useJob();
  const { user } = useAuth();

  // Form state
  const [formData, setFormData] = useState({
    barcode: '',
    manufacturer: '',
    model: '',
    description: '',
    serialNumber: '',
    purchaseDate: '',
    purchaseCost: '',
    notes: '',
    warehouse: '',
    rack: '',
    bay: '',
    quantity: 1  // Default quantity is 1
  });

  // Component state
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showSuccess, setShowSuccess] = useState(false);
  const [scanResult, setScanResult] = useState('');
  
  // Equipment selection state
  const [isAutoFilled, setIsAutoFilled] = useState(false);
  const [showTemplateSearch, setShowTemplateSearch] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);

  // Recent items
  const [recentItems, setRecentItems] = useState([]);
  const [recentLoading, setRecentLoading] = useState(false);

  // Batch created items for printing
  const [batchCreatedItems, setBatchCreatedItems] = useState([]); // Store batch created items
  
  // History Dialog state
  const [historyDialogOpen, setHistoryDialogOpen] = useState(false);
  const [selectedItemForHistory, setSelectedItemForHistory] = useState(null);
  
  // Edit/View Dialog state
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [editFormData, setEditFormData] = useState({
    warehouse_id: '',
    rack_id: '',
    bay_id: ''
  });
  const [saveError, setSaveError] = useState(null);
  const [saving, setSaving] = useState(false);
  
  // Smart assignment state
  const [suggestedJobs, setSuggestedJobs] = useState([]);
  const [showJobSelectionDialog, setShowJobSelectionDialog] = useState(false);
  const [pendingEquipmentData, setPendingEquipmentData] = useState(null);
  const [selectedJobForAssignment, setSelectedJobForAssignment] = useState(null);
  const [dialogQuantity, setDialogQuantity] = useState(1);

  // Search suggestions state
  const [searchSuggestions, setSearchSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);

  // Load recent items on component mount
  useEffect(() => {
    loadRecentItems();
  }, []);

  // Auto-clear scanResult messages after 10 seconds to prevent stale messages
  useEffect(() => {
    if (scanResult) {
      const timer = setTimeout(() => {
        setScanResult('');
      }, 10000); // Clear after 10 seconds

      return () => clearTimeout(timer);
    }
  }, [scanResult]);

  const loadRecentItems = async () => {
    setRecentLoading(true);
    try {
      const response = await api.get('/equipment-items', {
        params: {
          sortBy: 'created_at',
          sortOrder: 'DESC',
          limit: 500, // Increased from 20 to 500 to support frontend pagination
          // Only show items that have been physically received (exclude "planned" status)
          excludeStatus: 'planned'
        }
      });
      setRecentItems(response.data?.data || []);
    } catch (error) {
      console.error('Error loading recent items:', error);
    } finally {
      setRecentLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Debounced search for equipment templates
  const searchEquipmentTemplates = useCallback(async (searchTerm) => {
    if (!searchTerm || searchTerm.length < 2) {
      setSearchSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    setSearchLoading(true);
    try {
      // Search templates by multiple fields
      const response = await api.get('/equipment-templates', {
        params: {
          search: searchTerm,
          limit: 10
        }
      });

      const templates = response.data?.data || response.data || [];

      // Only show templates for receiving workflow
      const suggestions = templates.map(template => ({
        type: 'template',
        id: template.id,
        title: template.description || template.name || 'Unknown Template',
        subtitle: `${template.manufacturer || ''} ${template.model || ''}`.trim() || 'No model info',
        barcode: template.barcode_pattern,
        data: template
      }));

      setSearchSuggestions(suggestions);
      setShowSuggestions(suggestions.length > 0);

    } catch (error) {
      console.error('Error searching equipment templates:', error);
      setSearchSuggestions([]);
      setShowSuggestions(false);
    } finally {
      setSearchLoading(false);
    }
  }, []);

  // Debounce the search function
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (formData.barcode && formData.barcode.length >= 2) {
        // Only search if it doesn't look like a traditional barcode
        const isTraditionalBarcode = /^[0-9]{8,}$/.test(formData.barcode);
        if (!isTraditionalBarcode) {
          searchEquipmentTemplates(formData.barcode);
        }
      }
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [formData.barcode, searchEquipmentTemplates]);

  // Handle selection of a search suggestion
  const handleSuggestionSelect = async (suggestion) => {
    setShowSuggestions(false);
    setSearchSuggestions([]);

    // If suggestion is null (click away), just hide suggestions
    if (!suggestion) {
      return;
    }

    // Handle template selection
    const template = suggestion.data;
    const templateData = {
      manufacturer: template.manufacturer || '',
      model: template.model || '',
      description: template.description || template.name || '',
      template_id: template.id
    };

    setFormData(prev => ({
      ...prev,
      barcode: template.barcode_pattern || formData.barcode,
      ...templateData
    }));

    setSelectedTemplate(template);
    setIsAutoFilled(true);
    setShowTemplateSearch(false);
    setScanResult(`✓ Selected template: ${template.name || template.description}`);

    // Trigger smart assignment with the full template object
    await handleSmartAssignmentWithTemplate(template);
  };

  const handleBarcodeChange = async (barcode) => {
    if (!barcode) return;

    setLoading(true);
    setError('');
    setScanResult('');
    
    try {
      // Try to find existing equipment with this barcode
      const response = await api.get(`/barcode/lookup`, {
        params: { barcode }
      });
      
      if (response.data?.found && response.data?.prefillData) {
        // Equipment found - auto-fill the form
        const equipment = response.data.prefillData;
        setFormData(prev => ({
          ...prev,
          barcode: barcode,
          manufacturer: equipment.manufacturer || '',
          model: equipment.model || '',
          description: equipment.description || ''
        }));
        
        setIsAutoFilled(true);
        setShowTemplateSearch(false);
        setScanResult(`Found existing equipment: ${equipment.description}`);
        
        // Trigger smart assignment with the found equipment data
        await handleSmartAssignmentWithTemplate(equipment);
      } else {
        // Equipment not found - check for templates with this barcode pattern
        try {
          const templateResponse = await api.get('/equipment-templates');
          const templates = templateResponse.data?.data || templateResponse.data || [];
          
          // console.log(`Checking ${templates.length} templates for barcode: ${barcode}`);
          
          // Find template with matching barcode pattern
          const matchingTemplate = templates.find(t => {
            // Check all possible barcode fields
            const matches = t.barcode === barcode ||
                          t.barcode_pattern === barcode || 
                          (t.barcode_pattern && barcode.startsWith(t.barcode_pattern));
            
            if (t.barcode === barcode || t.barcode_pattern === barcode) {
              // console.log(`Found exact match: ${t.name}`, t);
            }
            
            return matches;
          });
          
          if (matchingTemplate) {
            // Template found - auto-fill with template data
            const templateData = {
              manufacturer: matchingTemplate.manufacturer || '',
              model: matchingTemplate.model || '',
              description: matchingTemplate.description || matchingTemplate.name || '',
              template_id: matchingTemplate.id
            };
            
            setFormData(prev => ({
              ...prev,
              barcode: barcode,
              ...templateData
            }));
            
            setSelectedTemplate(matchingTemplate);
            setIsAutoFilled(true);
            setShowTemplateSearch(false);
            setScanResult(`✓ Found template: ${matchingTemplate.name}`);
            
            // Trigger smart assignment with the full template object
            await handleSmartAssignmentWithTemplate(matchingTemplate);
          } else {
            // No template found - show template search
            setFormData(prev => ({ ...prev, barcode: barcode }));
            setIsAutoFilled(false);
            setShowTemplateSearch(true);
            setScanResult(`Barcode ${barcode} not found. Please select or create a template.`);
          }
        } catch (templateError) {
          console.error('Error checking templates:', templateError);
          // Fall back to template search
          setFormData(prev => ({ ...prev, barcode: barcode }));
          setIsAutoFilled(false);
          setShowTemplateSearch(true);
          setScanResult(`Barcode ${barcode} not found. Please select or create a template.`);
        }
      }
    } catch (error) {
      // Handle any network errors
      setFormData(prev => ({ ...prev, barcode: barcode }));
      setIsAutoFilled(false);
      setShowTemplateSearch(true);
      setScanResult(`Barcode ${barcode} not found. Please select or create a template.`);
      setError('Error looking up barcode: ' + (error.message || 'Unknown error'));
    } finally {
      setLoading(false);
    }
  };

  const handleReceiveItem = async () => {
    if (!formData.barcode || (!isAutoFilled && !selectedTemplate)) {
      setError('Please scan a barcode and select equipment details');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const equipmentData = {
        barcode: formData.barcode,
        manufacturer: formData.manufacturer,
        model: String(formData.model), // Ensure model is always a string
        description: formData.description,
        serial_number: formData.serialNumber,
        purchase_date: formData.purchaseDate || null,
        purchase_cost: formData.purchaseCost ? parseFloat(formData.purchaseCost) : null,
        notes: formData.notes,
        status: 'available',
        received_by: user?.email || 'warehouse.user@bladesav.com'
      };
      
      // Add location fields if they have values (can be UUIDs or string IDs like 'rack-10', 'bay-A')
      if (formData.warehouse) {
        equipmentData.warehouse_id = formData.warehouse;
      }
      if (formData.rack) {
        equipmentData.rack_id = formData.rack;
      }
      if (formData.bay) {
        equipmentData.bay_id = formData.bay;
      }
      if (selectedJobForAssignment) {
        equipmentData.current_job_id = selectedJobForAssignment.jobId || selectedJobForAssignment.id;
      }

      // Create multiple items based on quantity
      const quantity = parseInt(formData.quantity) || 1;
      const itemsToCreate = [];
      
      for (let i = 0; i < quantity; i++) {
        const itemData = { ...equipmentData };

        // Don't append suffix to description - each item is unique by equipment_id
        // Don't append suffix to serial numbers - that doesn't make sense
        // Each item either has its own unique serial or no serial

        itemsToCreate.push(itemData);
      }
      

      // Check if all items have received_by
      const missingReceivedBy = itemsToCreate.filter(item => !item.received_by);
      if (missingReceivedBy.length > 0) {
        console.error('Items missing received_by:', missingReceivedBy);
      }

      // Backend expects an array or { items: [...] }
      const response = await api.post('/equipment-items', itemsToCreate);

      // console.log('API Response:', response.data);

      if (response.data) {
        // Handle different response formats
        let createdItems;
        if (Array.isArray(response.data)) {
          createdItems = response.data;
        } else if (response.data.data && Array.isArray(response.data.data)) {
          createdItems = response.data.data;
        } else if (response.data.items && Array.isArray(response.data.items)) {
          createdItems = response.data.items;
        } else {
          // Single item response wrapped in an array
          createdItems = [response.data];
        }

        // console.log('Processed createdItems:', createdItems);
        const createdItem = createdItems[0]; // Use first item for compatibility

        // If equipment was assigned to a job via smart assignment, assign ALL created items
        if (selectedJobForAssignment && createdItems.length > 0) {
          try {
            const jobId = selectedJobForAssignment.jobId || selectedJobForAssignment.id;
            const siteId = selectedJobForAssignment.siteId;

            // Assign each created item individually
            const assignmentPromises = createdItems
              .filter(item => item.id) // Only assign items with valid IDs
              .map(item => {
                const assignmentData = {
                  inventoryId: item.id.toString(),
                  quantity: 1,
                  assignmentType: 'smart_assignment',
                  notes: 'Assigned via smart assignment feature'
                };

                // Include siteId if this is a multi-site job
                if (siteId) {
                  assignmentData.siteId = siteId;
                }

                return api.post(`/jobs/${jobId}/equipment/assign`, assignmentData);
              });

            // Wait for all assignments to complete
            await Promise.all(assignmentPromises);

            const siteInfo = siteId && selectedJobForAssignment.siteName ? ` - ${selectedJobForAssignment.siteName}` : '';
            const itemCount = createdItems.filter(item => item.id).length;
            setScanResult(`${itemCount} item(s) received and assigned to Job ${jobId}${siteInfo}: ${selectedJobForAssignment.name || 'Unknown'}`);
          } catch (assignError) {
            console.error('Error assigning equipment to job:', assignError);
            // Show the actual error to help with troubleshooting
            const errorMsg = assignError.response?.data?.error || assignError.message || 'Unknown error';
            setScanResult(`⚠️ Equipment received but assignment failed: ${errorMsg}`);
            setError(`Assignment failed: ${errorMsg}`);
          }
        }
        
        // Store batch items if multiple were created
        if (createdItems && createdItems.length > 1) {
          // console.log('Setting batch created items:', createdItems);
          setBatchCreatedItems(createdItems);
          setScanResult(`Successfully received ${createdItems.length} items. QR codes will print automatically.`);

          // Always auto-print batch QR codes (per user request) - but only for items with valid IDs
          const validItems = createdItems.filter(item => item.id || item.equipment_id);
          if (validItems.length > 0) {
            // console.log('Auto-printing batch QR codes for', validItems.length, 'valid items');
            // Don't use setTimeout - call directly to avoid popup blocker
            handlePrintBatchQRWithItems(validItems);
          } else {
          }
        } else if (createdItems && createdItems.length === 1) {
          // For single item, auto-print QR code
          const validItems = createdItems.filter(item => item.id || item.equipment_id);
          if (validItems.length > 0) {
            await handlePrintBatchQRWithItems(validItems);
          }
        }
        
        setShowSuccess(true);
        
        // Reset form
        setFormData({
          barcode: '',
          manufacturer: '',
          model: '',
          description: '',
          serialNumber: '',
          purchaseDate: '',
          purchaseCost: '',
          notes: '',
          warehouse: '',
          rack: '',
          bay: '',
          quantity: 1
        });
        
        setIsAutoFilled(false);
        setShowTemplateSearch(false);
        setSelectedTemplate(null);
        setScanResult('');
        
        // Reset smart assignment state
        setSelectedJobForAssignment(null);
        setSuggestedJobs([]);
        setShowJobSelectionDialog(false);
        setPendingEquipmentData(null);
        
        // Reload recent items
        await loadRecentItems();

        // Refresh inventory counts - this is crucial for updating available quantities
        if (loadInventorySummary) {
          await loadInventorySummary();
        }

        // Scroll back to top after successful receive
        const mainContent = document.querySelector('main');
        if (mainContent) {
          mainContent.scrollTo({ top: 0, behavior: 'smooth' });
        }
      }
    } catch (error) {
      console.error('Error receiving equipment:', error);
      console.error('Error response:', error.response?.data);

      // Log detailed validation errors
      if (error.response?.data?.errors) {
        console.error('Validation errors detail:', error.response.data.errors);
        const errorMessages = error.response.data.errors.map(e => {
          const field = e.field || e.path || e.param || 'unknown';
          const msg = e.msg || e.message || 'validation failed';
          const value = e.value !== undefined ? ` (value: ${JSON.stringify(e.value)})` : '';
          return `${field}: ${msg}${value}`;
        }).join(', ');
        setError(`Validation errors: ${errorMessages}`);
      } else {
        setError('Error receiving equipment: ' + (error.response?.data?.error || error.message || 'Unknown error'));
      }
    } finally {
      setLoading(false);
    }
  };

  // Helper functions to get warehouse and rack names
  const getWarehouseName = (warehouseId) => {
    if (!warehouseId) return 'Not assigned';
    const warehouse = warehouses.find(w => w.id === warehouseId);
    return warehouse ? warehouse.name : warehouseId;
  };

  const getRackName = (rackId) => {
    if (!rackId) return 'Not assigned';
    const rack = racks.find(r => r.id === rackId);
    return rack ? rack.name : rackId;
  };

  const getBayName = (bayId) => {
    if (!bayId) return 'Not assigned';
    const bay = bays.find(b => b.id === bayId);
    return bay ? bay.name : bayId;
  };

  // Smart assignment logic using template data
  const handleSmartAssignmentWithTemplate = async (template) => {
    try {

      if (template && template.found !== false) {

        // Find jobs that expect this equipment type using smart assignment
        const queryParams = {
          manufacturer: template.manufacturer,
          model: template.model,
          templateId: template.id,
          barcode: template.barcode_pattern || template.barcode || formData.barcode // Use template's barcode pattern or barcode field
        };

        const equipmentId = encodeURIComponent(template.description || template.name || 'unknown');
        const jobsResponse = await api.get(`/plans/equipment/${equipmentId}/jobs`, {
          params: queryParams
        });

        
        const allJobs = jobsResponse.data?.data?.jobs || jobsResponse.data?.jobs || jobsResponse.data?.data || jobsResponse.data || [];
        const filteredJobs = Array.isArray(allJobs) ? allJobs.filter(job => 
          job.status === 'Active' || job.status === 'Upcoming' || job.status === 'Planning' || 
          job.jobStatus === 'Active' || job.jobStatus === 'Upcoming' || job.jobStatus === 'Planning'
        ) : [];
        
        // Process jobs
        const processedJobs = [];
        filteredJobs.forEach(job => {
          processedJobs.push({
            ...job,
            matchingPlans: job.matchingPlans || []
          });
        });
        
        // Sort by job ID
        processedJobs.sort((a, b) => {
          const jobCompare = (a.jobId || a.id || '').localeCompare(b.jobId || b.id || '');
          if (jobCompare !== 0) return jobCompare;
          return (a.siteName || '').localeCompare(b.siteName || '');
        });
        
        // Trust the backend's matching logic - it already filtered by manufacturer/model/barcode
        // The backend SQL query handles the matching, so we don't need to re-filter here
        // This ensures site-level plans are included
        const expectingJobs = processedJobs.filter(job => job.matchingPlans?.length > 0);

        if (expectingJobs.length > 0) {

          if (expectingJobs.length === 1) {
            // Auto-select the single job but still show the dialog for confirmation and quantity adjustment
            const singleJob = expectingJobs[0];
            setSelectedJobForAssignment(singleJob);
            setSuggestedJobs(expectingJobs);
            showJobSelectionPrompt(template);

            // Auto-populate stack location from the pre-selected job
            // Try new multi-location format first, then fall back to old single fields
            const firstLocation = singleJob.stackLocations?.[0];
            if (firstLocation || singleJob.stackWarehouseId || singleJob.stackRackId || singleJob.stackBayId) {
              setFormData(prev => ({
                ...prev,
                warehouse: firstLocation?.warehouseId || singleJob.stackWarehouseId || prev.warehouse || '',
                rack: firstLocation?.rackId || singleJob.stackRackId || prev.rack || '',
                bay: firstLocation?.bayId || singleJob.stackBayId || prev.bay || ''
              }));
            }
          } else {
            // Multiple jobs - show selection dialog without pre-selection
            setSuggestedJobs(expectingJobs);
            showJobSelectionPrompt(template);
          }
        } else {
          // console.log('No jobs found expecting this equipment type');
        }
      }
    } catch (error) {
      console.error('Error in smart assignment lookup:', error);
    }
  };

  // Auto-assign equipment to job
  const autoAssignToJob = async (job, template) => {
    try {
      // console.log('Auto-assigning equipment to job:', job.jobName || job.name);
      
      // If this job has matching plans, use the equipment details from the first matching plan
      if (job.matchingPlans && job.matchingPlans.length > 0) {
        const plan = job.matchingPlans[0];
        // console.log('Using equipment details from matching plan:', plan);
        
        // Update form data with plan details if they exist
        setFormData(prev => ({
          ...prev,
          manufacturer: plan.manufacturer || template?.manufacturer || prev.manufacturer || '',
          model: plan.model || template?.model || prev.model || '',
          description: plan.description || plan.equipment_name || template?.description || prev.description || ''
        }));
      }
      
      // Set assignment info
      setSelectedJobForAssignment(job);
      
      // Auto-populate stack location from job if available
      // Try new multi-location format first, then fall back to old single fields
      const firstLocation = job.stackLocations?.[0];
      if (firstLocation || job.stackWarehouseId || job.stackRackId || job.stackBayId) {
        setFormData(prev => ({
          ...prev,
          warehouse: firstLocation?.warehouseId || job.stackWarehouseId || prev.warehouse || '',
          rack: firstLocation?.rackId || job.stackRackId || prev.rack || '',
          bay: firstLocation?.bayId || job.stackBayId || prev.bay || ''
        }));
      }
      
      // Show success message
      setError('');
      setScanResult(`Equipment will be assigned to Job ${job.jobId || job.id}: ${job.jobName || job.name || 'Unknown'}`);
    } catch (error) {
      console.error('Error in auto-assignment:', error);
    }
  };

  // Show job selection prompt
  const showJobSelectionPrompt = (template) => {
    setPendingEquipmentData({ template, barcode: formData.barcode });
    setDialogQuantity(formData.quantity || 1); // Initialize with current form quantity
    setShowJobSelectionDialog(true);
  };

  // Handle manual job selection from dialog
  const handleManualJobSelection = async (job) => {
    try {
      setSelectedJobForAssignment(job);
      setShowJobSelectionDialog(false);

      // Auto-populate stack location from job if available
      // Try new multi-location format first, then fall back to old single fields
      const firstLocation = job.stackLocations?.[0];
      if (firstLocation || job.stackWarehouseId || job.stackRackId || job.stackBayId) {
        setFormData(prev => ({
          ...prev,
          warehouse: firstLocation?.warehouseId || job.stackWarehouseId || prev.warehouse || '',
          rack: firstLocation?.rackId || job.stackRackId || prev.rack || '',
          bay: firstLocation?.bayId || job.stackBayId || prev.bay || ''
        }));
      }

      // If this job has matching plans, use the equipment details from the first matching plan
      if (job.matchingPlans && job.matchingPlans.length > 0) {
        const plan = job.matchingPlans[0];
        // console.log('Using equipment details from matching plan:', plan);

        // Update form data with plan details if they exist
        setFormData(prev => ({
          ...prev,
          manufacturer: plan.manufacturer || prev.manufacturer || '',
          model: plan.model || prev.model || '',
          description: plan.description || plan.equipment_name || prev.description || ''
        }));
      }

      setScanResult(`Equipment will be assigned to Job ${job.jobId || job.id}: ${job.jobName || job.name || 'Unknown'}`);
    } catch (error) {
      console.error('Error in manual job selection:', error);
    }
  };

  // Handle skipping job assignment - proceed with receiving without assignment
  const handleSkipAssignment = () => {
    try {
      // Clear any selected job assignment
      setSelectedJobForAssignment(null);
      setShowJobSelectionDialog(false);

      // Clear smart assignment state
      setSuggestedJobs([]);
      setPendingEquipmentData(null);

      // Provide clear feedback that assignment was skipped
      const quantity = dialogQuantity || 1;
      setScanResult(`✓ Job assignment skipped. ${quantity} item${quantity > 1 ? 's' : ''} will be received without assignment and can be assigned later.`);

    } catch (error) {
      console.error('Error in skip assignment:', error);
    }
  };

  const handleEditItem = (item) => {
    setEditingItem(item);
    setEditFormData({
      warehouse_id: item.warehouse_id || '',
      rack_id: item.rack_id || '',
      bay_id: item.bay_id || ''
    });
    setSaveError(null);
    setEditDialogOpen(true);
  };

  const handleEditClose = () => {
    setEditDialogOpen(false);
    setEditingItem(null);
    setEditFormData({
      warehouse_id: '',
      rack_id: '',
      bay_id: ''
    });
    setSaveError(null);
  };

  const handleEditFormChange = (field, value) => {
    setEditFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleEditSave = async () => {
    try {
      setSaving(true);
      setSaveError(null);
      
      const updateData = {
        warehouse_id: editFormData.warehouse_id || null,
        rack_id: editFormData.rack_id || null,
        bay_id: editFormData.bay_id || null
      };
      
      
      const response = await api.put(`/equipment-items/${editingItem.id}`, updateData);
      
      if (response.data) {
        // Reload recent items to show updated data
        await loadRecentItems();
        handleEditClose();
      }
    } catch (error) {
      console.error('Error updating equipment:', error);
      setSaveError('Error updating equipment: ' + (error.response?.data?.error || error.message || 'Unknown error'));
    } finally {
      setSaving(false);
    }
  };

  const handleViewHistory = (item) => {
    setSelectedItemForHistory(item);
    setHistoryDialogOpen(true);
  };


  const handlePrintQR = async (item) => {
    // Fetch full item details to ensure we have job/site names
    try {
      const itemId = item.id || item.equipment_id;
      const response = await api.get(`/equipment-items/${itemId}`);
      const itemWithDetails = response.data?.data?.data || response.data?.data || response.data || item;
      await printSingleQRCode(itemWithDetails);
    } catch (error) {
      console.error('Error fetching item details for QR:', error);
      // Fall back to using the item as-is
      await printSingleQRCode(item);
    }
  };
  
  // Print batch QR codes with items passed as parameter (for auto-print)
  const handlePrintBatchQRWithItems = async (items) => {
    if (!items || items.length === 0) {
      return;
    }

    // Fetch complete details for each item to ensure job information is included
    const itemsWithDetails = await Promise.all(
      items.map(async (item) => {
        try {
          const itemId = item.id || item.equipment_id;
          if (!itemId) {
            return item;
          }

          const response = await api.get(`/equipment-items/${itemId}`);
          const detailedItem = response.data?.data?.data || response.data?.data || response.data || item;
          return detailedItem;
        } catch (error) {
          console.error('Error fetching details for item:', item, error);
          return item; // Fall back to original item
        }
      })
    );

    // Use centralized print service
    await printQRCodes(itemsWithDetails);
  };

  const handlePrintBatchQR = async () => {
    // Print batch QR codes directly using centralized service
    if (!batchCreatedItems || batchCreatedItems.length === 0) {
      return;
    }

    await printQRCodes(batchCreatedItems);

    // Clear batch items after printing
    setBatchCreatedItems([]);
  };

  return (
    <Box>
      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {/* Success notification for barcode scan */}
      {scanResult && (
        <Alert 
          severity="success"
          sx={{ 
            position: 'fixed',
            bottom: 20,
            left: 20,
            right: 20,
            zIndex: 2000,
            boxShadow: 3
          }}
          onClose={() => setScanResult('')}
        >
          {scanResult}
        </Alert>
      )}

      {/* Main Form */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {/* Step 1: Barcode */}
          <ReceiveBarcodeStep
            formData={formData}
            setFormData={setFormData}
            onBarcodeChange={handleBarcodeChange}
            isAutoFilled={isAutoFilled}
            scanResult={scanResult}
            searchSuggestions={searchSuggestions}
            showSuggestions={showSuggestions}
            searchLoading={searchLoading}
            onSuggestionSelect={handleSuggestionSelect}
          />

          {/* Only show remaining steps after barcode is entered */}
          {formData.barcode && (
            <>
              {/* Step 2: Equipment Selection */}
              <ReceiveEquipmentStep
                formData={formData}
                setFormData={setFormData}
                isAutoFilled={isAutoFilled}
                showTemplateSearch={showTemplateSearch}
                selectedTemplate={selectedTemplate}
                setSelectedTemplate={setSelectedTemplate}
                onTemplateCreate={(template) => {
                }}
                onTemplateSelect={handleSmartAssignmentWithTemplate}
              />

              {/* Steps 3-4 only when equipment is selected */}
              {(isAutoFilled || selectedTemplate) && (
                <>
                  {/* Step 3: Location Assignment */}
                  <ReceiveLocationStep
                    formData={formData}
                    handleChange={handleChange}
                    warehouses={warehouses}
                    racks={racks}
                    bays={bays}
                    selectedJobForAssignment={selectedJobForAssignment}
                  />

                  {/* Step 4: Additional Information */}
                  <ReceiveDetailsStep
                    formData={formData}
                    handleChange={handleChange}
                  />

                  {/* Submit Button */}
                  <Box>
                    <Button 
                      variant="contained" 
                      color="primary" 
                      onClick={handleReceiveItem}
                      startIcon={<SaveIcon />}
                      fullWidth
                      disabled={loading}
                      sx={{ 
                        mt: 2,
                        height: '56px',
                        fontSize: '1rem'
                      }}
                    >
                      {loading ? <CircularProgress size={24} color="inherit" /> : `Receive ${formData.quantity > 1 ? formData.quantity + ' Items' : 'Item'}`}
                    </Button>
                    
                    {/* Debug: Show batch items count */}
                    {batchCreatedItems.length > 0 && (
                      <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                        {batchCreatedItems.length} items ready to print
                      </Typography>
                    )}

                    {/* Batch Print Button - shows after batch receive */}
                    {batchCreatedItems.length > 0 && (
                      <Button
                        variant="outlined"
                        color="secondary"
                        onClick={handlePrintBatchQR}
                        fullWidth
                        sx={{ 
                          mt: 2,
                          height: '48px',
                          fontSize: '0.9rem'
                        }}
                      >
                        🖨️ Print All {batchCreatedItems.length} QR Code Labels
                      </Button>
                    )}
                  </Box>
                </>
              )}
            </>
          )}
        </Box>
      </Paper>

      {/* Recent Items */}
      <ReceiveRecentItems
        recentItems={recentItems}
        onRefresh={loadRecentItems}
        onEdit={handleEditItem}
        onViewHistory={handleViewHistory}
        onPrintQR={handlePrintQR}
        onPrintBulkQR={handlePrintBatchQRWithItems}
        loading={recentLoading}
        warehouses={warehouses}
        racks={racks}
        bays={bays}
      />

      {/* Success notification */}
      <Snackbar 
        open={showSuccess} 
        autoHideDuration={6000} 
        onClose={() => setShowSuccess(false)}
        message="Equipment received successfully"
      />

      {/* Status History Dialog */}
      <StatusHistoryDialog
        open={historyDialogOpen}
        onClose={() => setHistoryDialogOpen(false)}
        equipment={selectedItemForHistory}
      />

      {/* Equipment Details Dialog */}
      <Dialog open={editDialogOpen} onClose={handleEditClose} maxWidth="sm" fullWidth>
        <DialogTitle>
          Edit Equipment: {editingItem?.equipment_id || editingItem?.id}
        </DialogTitle>
        <DialogContent>
          {saveError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {saveError}
            </Alert>
          )}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            <TextField
              label="Equipment ID"
              value={editingItem?.equipment_id || editingItem?.id || ''}
              fullWidth
              InputProps={{ readOnly: true }}
              variant="outlined"
            />
            <TextField
              label="Description"
              value={editingItem?.description || editingItem?.name || ''}
              fullWidth
              InputProps={{ readOnly: true }}
              variant="outlined"
            />
            <TextField
              label="Manufacturer"
              value={editingItem?.manufacturer || 'Not specified'}
              fullWidth
              InputProps={{ readOnly: true }}
              variant="outlined"
            />
            <TextField
              label="Model"
              value={editingItem?.model || 'Not specified'}
              fullWidth
              InputProps={{ readOnly: true }}
              variant="outlined"
            />
            <TextField
              label="Serial Number"
              value={editingItem?.serial_number || 'Not specified'}
              fullWidth
              InputProps={{ readOnly: true }}
              variant="outlined"
            />
            <TextField
              label="Barcode"
              value={editingItem?.barcode || 'Not specified'}
              fullWidth
              InputProps={{ readOnly: true }}
              variant="outlined"
            />
            <TextField
              label="Status"
              value={editingItem?.status || 'Unknown'}
              fullWidth
              InputProps={{ readOnly: true }}
              variant="outlined"
            />
            
            {/* Editable Location Fields */}
            <FormControl fullWidth variant="outlined">
              <InputLabel>Warehouse</InputLabel>
              <Select
                value={editFormData.warehouse_id}
                onChange={(e) => handleEditFormChange('warehouse_id', e.target.value)}
                label="Warehouse"
              >
                <MenuItem value="">
                  <em>No warehouse</em>
                </MenuItem>
                {warehouses.map((warehouse) => (
                  <MenuItem key={warehouse.id} value={warehouse.id}>
                    {warehouse.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth variant="outlined">
              <InputLabel>Rack</InputLabel>
              <Select
                value={editFormData.rack_id}
                onChange={(e) => handleEditFormChange('rack_id', e.target.value)}
                label="Rack"
              >
                <MenuItem value="">
                  <em>No rack</em>
                </MenuItem>
                {racks.map((rack) => (
                  <MenuItem key={rack.id} value={rack.id}>
                    {rack.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth variant="outlined">
              <InputLabel>Bay</InputLabel>
              <Select
                value={editFormData.bay_id}
                onChange={(e) => handleEditFormChange('bay_id', e.target.value)}
                label="Bay"
              >
                <MenuItem value="">
                  <em>No bay</em>
                </MenuItem>
                {bays.map((bay) => (
                  <MenuItem key={bay.id} value={bay.id}>
                    {bay.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            
            {editingItem?.notes && (
              <Box>
                <Typography variant="caption" color="text.secondary" gutterBottom>
                  Notes
                </Typography>
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <LinkifiedText 
                    text={editingItem.notes}
                    typographyProps={{ 
                      variant: "body2"
                    }}
                  />
                </Paper>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleEditClose}>Cancel</Button>
          <Button 
            onClick={handleEditSave} 
            variant="contained"
            disabled={saving}
          >
            {saving ? <CircularProgress size={20} /> : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Job Selection Dialog */}
      <Dialog 
        open={showJobSelectionDialog} 
        onClose={() => setShowJobSelectionDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Smart Assignment: Job Assignment
          <Box component="div" sx={{ mt: 1 }}>
            <Typography variant="subtitle2" color="text.secondary" component="span">
              You are receiving {dialogQuantity || 1} item{(dialogQuantity || 1) > 1 ? 's' : ''} of this equipment type
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            {suggestedJobs.length === 1
              ? 'This equipment matches a job requirement. Would you like to assign it to this job?'
              : 'Multiple jobs need this equipment type. Please select which job to assign it to:'}
          </Typography>
          
          <Paper elevation={1} sx={{ p: 2, mb: 2, backgroundColor: 'grey.50' }}>
            <Typography variant="subtitle2" sx={{ mb: 2 }}>
              Equipment: <strong>{pendingEquipmentData?.template?.description || 'Unknown'}</strong>
              {pendingEquipmentData?.template?.manufacturer && (
                <> - {pendingEquipmentData.template.manufacturer} {pendingEquipmentData?.template?.model}</>
              )}
            </Typography>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <PackageIcon fontSize="small" color="primary" />
                <Typography variant="body2" color="primary.main" sx={{ fontWeight: 600 }}>
                  Receiving Quantity:
                </Typography>
              </Box>
              <TextField
                type="number"
                value={dialogQuantity === 0 ? '' : dialogQuantity}
                onChange={(e) => {
                  const value = e.target.value;
                  if (value === '') {
                    // Allow empty state while typing
                    setDialogQuantity(0);
                    setFormData(prev => ({ ...prev, quantity: 1 })); // Keep form valid with minimum 1
                  } else {
                    const newQuantity = Math.max(1, parseInt(value) || 1);
                    setDialogQuantity(newQuantity);
                    setFormData(prev => ({ ...prev, quantity: newQuantity }));
                  }
                }}
                onBlur={() => {
                  // Ensure we have a valid quantity when user leaves the field
                  if (dialogQuantity === 0) {
                    setDialogQuantity(1);
                    setFormData(prev => ({ ...prev, quantity: 1 }));
                  }
                }}
                size="small"
                sx={{ width: 80 }}
                inputProps={{ min: 1 }}
                placeholder="1"
              />
              <Typography variant="body2" color="text.secondary">
                item{(dialogQuantity || 1) > 1 ? 's' : ''}
              </Typography>
            </Box>
          </Paper>

          <Grid container spacing={2} sx={{ mt: 1 }}>
            {suggestedJobs.map((job) => {
              // Check if this job+site combination is selected
              const isSelected = selectedJobForAssignment?.jobId === job.jobId &&
                                selectedJobForAssignment?.siteId === job.siteId;

              return (
              <Grid item xs={12} sm={6} key={`${job.jobId || job.id}-${job.siteId || 'job'}`}>
                <Card
                  sx={{
                    cursor: 'pointer',
                    border: isSelected ? '2px solid' : '1px solid',
                    borderColor: isSelected ? 'primary.main' : 'divider',
                    '&:hover': {
                      elevation: 2,
                      borderColor: 'primary.main'
                    }
                  }}
                  onClick={() => {
                    setSelectedJobForAssignment(job);
                    // Auto-populate stack location from job if available
                    // Try new multi-location format first, then fall back to old single fields
                    const firstLocation = job.stackLocations?.[0];
                    if (firstLocation || job.stackWarehouseId || job.stackRackId || job.stackBayId) {
                      setFormData(prev => ({
                        ...prev,
                        warehouse: firstLocation?.warehouseId || job.stackWarehouseId || prev.warehouse || '',
                        rack: firstLocation?.rackId || job.stackRackId || prev.rack || '',
                        bay: firstLocation?.bayId || job.stackBayId || prev.bay || ''
                      }));
                    }
                  }}
                >
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                      <Typography variant="h6" component="div">
                        Job {job.jobId || job.id}
                      </Typography>
                      <Chip 
                        label={job.status || job.jobStatus || 'Active'} 
                        size="small" 
                        color={job.status === 'Active' || job.jobStatus === 'Active' ? 'success' : 'default'}
                      />
                    </Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      {job.jobName || job.name || 'Unnamed Job'}
                    </Typography>
                    {job.siteName && (
                      <Typography variant="caption" color="text.secondary" display="block">
                        Site: {job.siteName}
                      </Typography>
                    )}
                    {job.matchingPlans && job.matchingPlans.length > 0 && (
                      <Box sx={{ mt: 0.5 }}>
                        {job.matchingPlans.map((plan, planIndex) => (
                          <Box key={planIndex} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <PackageIcon fontSize="inherit" color="action" />
                              <Typography variant="caption" color="text.secondary">
                                Planned: {plan.planned_quantity || plan.needed_quantity || 1}
                              </Typography>
                            </Box>
                            <Typography variant="caption" color="text.secondary">•</Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <CheckCircleIcon fontSize="inherit" color="success" />
                              <Typography variant="caption" color="text.secondary">
                                Received: {plan.assigned_quantity || 0}
                              </Typography>
                            </Box>
                            <Typography variant="caption" color="text.secondary">•</Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <PendingIcon fontSize="inherit" color="warning" />
                              <Typography variant="caption" color="text.secondary">
                                Still needed: {(plan.planned_quantity || plan.needed_quantity || 1) - (plan.assigned_quantity || 0)}
                              </Typography>
                            </Box>
                          </Box>
                        ))}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                          <PackageIcon fontSize="small" color="primary" />
                          <Typography variant="caption" color="primary.main" sx={{ fontWeight: 600 }}>
                            Receiving now: {dialogQuantity || 1} item{(dialogQuantity || 1) > 1 ? 's' : ''}
                          </Typography>
                        </Box>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Grid>
              );
            })}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleSkipAssignment}>
            Skip Assignment
          </Button>
          <Button
            onClick={() => selectedJobForAssignment && handleManualJobSelection(selectedJobForAssignment)}
            variant="contained"
            disabled={!selectedJobForAssignment}
          >
            Assign to Selected Job
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ReceiveInventory;